/**
 * Where the on-screen keyboard is, as two CSS variables on <html>:
 *
 *   --kb-inset  how far the visible area's bottom sits above the layout
 *               viewport's bottom — the keyboard's height; 0px when it is down
 *   --vv-top    how far the browser has panned the visible area down to show
 *               a focused field (iOS does this); 0px otherwise
 *
 * Why it is needed: on phones every dialog and sheet docks to the bottom
 * edge — exactly where the keyboard comes up. Chrome on Android (108+) and
 * Safari both shrink only the VISUAL viewport for the keyboard, and
 * `position: fixed; bottom: 0` stays glued to the layout viewport, i.e. behind
 * the keyboard. The sale dialog's "cash received" field, focused on open,
 * would sit under it. `interactive-widget=resizes-content` would fix Android
 * alone and would also lift the tab bar onto every keyboard in the app;
 * the visualViewport API is the one thing both browsers report.
 *
 * Pinch-zoom shrinks the visual viewport too — while zoomed the numbers would
 * describe the zoom, not a keyboard, so they stay 0.
 */

/** Smaller than any keyboard; absorbs toolbar and rounding noise. */
const MIN_KEYBOARD = 80

export function trackKeyboardInset() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv) return
  const root = document.documentElement
  let raf = 0
  let lastInset = -1
  let lastTop = -1

  const update = () => {
    raf = 0
    const zoomed = Math.abs(vv.scale - 1) > 0.01
    const raw = window.innerHeight - vv.height - vv.offsetTop
    const inset = zoomed || raw < MIN_KEYBOARD ? 0 : Math.round(raw)
    const top = zoomed || inset === 0 ? 0 : Math.max(0, Math.round(vv.offsetTop))
    if (inset !== lastInset) { lastInset = inset; root.style.setProperty('--kb-inset', `${inset}px`) }
    if (top !== lastTop) { lastTop = top; root.style.setProperty('--vv-top', `${top}px`) }
  }
  const schedule = () => { if (!raf) raf = requestAnimationFrame(update) }

  vv.addEventListener('resize', schedule)
  vv.addEventListener('scroll', schedule)
  update()
}
